import classNames from 'classnames'
import { FC, RefObject, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as DoubleCheckIcon } from 'src/assets/svg/double-check-white.svg'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import styles from './moduleFilterDropdown.module.scss'
import { MODULE_META } from '../ReportCard/moduleMeta'
import { ReportModule } from '../interfaces/report.interface'

// Display order, matching the design. Social and AI Listening have no feature
// flag — they are available to everyone.
const MODULE_ORDER: ReportModule[] = [
  'social',
  'ai',
  'inbox',
  'review',
  'content',
  'ad',
]

interface ModuleFilterDropdownProps {
  onApply: () => void
  // Fires on every tick so the parent's Filters pill can track the selection
  // live. Applying it to the list is the parent's call, on onApply.
  onChange: (modules: ReportModule[]) => void
  onClose: () => void
  selectedModules: ReportModule[]
  // The button that opens this panel. Excluded from the outside-click check:
  // mousedown on it would otherwise close the panel, and the click that follows
  // would reopen it, so the trigger appeared not to toggle.
  triggerRef?: RefObject<HTMLElement>
}

export const ModuleFilterDropdown: FC<ModuleFilterDropdownProps> = ({
  onApply,
  onChange,
  onClose,
  selectedModules,
  triggerRef,
}) => {
  const { data: userFeatures } = useGetUserFeaturesQuery()
  const features = userFeatures?.data

  // Only offer modules this company actually has. Defaulting to true keeps the
  // panel populated while the features query is still in flight, rather than
  // briefly claiming the company has no modules at all.
  const availableModules = MODULE_ORDER.filter((module) => {
    switch (module) {
      case 'ad':
        return (
          (features?.adsLibraryEnabled ?? true) ||
          (features?.adsPerformanceEnabled ?? true)
        )
      case 'content':
        return features?.contentAnalysisEnabled ?? true
      case 'inbox':
        return features?.inboxAnalysisEnabled ?? true
      case 'review':
        return features?.reviewAnalysisEnabled ?? true
      default:
        return true
    }
  })

  const draft = selectedModules

  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        !dropdownRef.current?.contains(target) &&
        !triggerRef?.current?.contains(target)
      ) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    // Deferred to the next tick so the click that opened the panel does not
    // immediately close it again.
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    })
    document.addEventListener('keydown', handleEscape)

    return () => {
      window.clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose, triggerRef])

  const areAllSelected =
    availableModules.length > 0 && draft.length === availableModules.length

  const toggleModule = (module: ReportModule) => {
    onChange(
      draft.includes(module)
        ? draft.filter((id) => id !== module)
        : [...draft, module]
    )
  }

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div className={styles.header}>
        <h4 className={styles.title}>Filter by module</h4>

        <button
          aria-label="Close filters"
          className={styles.closeButton}
          onClick={onClose}
          type="button"
        >
          <FontAwesomeIcon className={styles.closeIcon} icon={faTimes} />
        </button>
      </div>

      <div className={styles.selectAllRow}>
        <button
          className={styles.selectAllLeft}
          onClick={() => onChange(areAllSelected ? [] : availableModules)}
          type="button"
        >
          <span
            className={classNames(styles.checkbox, {
              [styles.checkboxChecked]: areAllSelected,
            })}
          >
            {areAllSelected && (
              <DoubleCheckIcon className={styles.checkboxIcon} />
            )}
          </span>
          <span>Select All</span>
          <span className={styles.selectAllDot} />
          <span className={styles.selectAllCount}>
            {availableModules.length}
          </span>
        </button>

        <span className={styles.selectedCount}>{draft.length} selected</span>
      </div>

      <div className={styles.moduleList}>
        {availableModules.map((module) => {
          const { color, icon: Icon, label } = MODULE_META[module]
          const isSelected = draft.includes(module)

          return (
            <button
              className={classNames(styles.moduleRow, {
                [styles.moduleRowSelected]: isSelected,
              })}
              key={module}
              onClick={() => toggleModule(module)}
              type="button"
            >
              <span
                className={classNames(styles.checkbox, {
                  [styles.checkboxChecked]: isSelected,
                })}
              >
                {isSelected && (
                  <DoubleCheckIcon className={styles.checkboxIcon} />
                )}
              </span>

              <Icon className={styles.moduleIcon} style={{ color }} />
              <span className={styles.moduleLabel} style={{ color }}>
                {label}
              </span>
            </button>
          )
        })}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.clearButton}
          disabled={!draft.length}
          onClick={() => onChange([])}
          type="button"
        >
          Clear all
        </button>

        <button className={styles.applyButton} onClick={onApply} type="button">
          Apply
        </button>
      </div>
    </div>
  )
}
